import { createServerFn } from "@tanstack/react-start";

import type { MemberDetailResponse, MemberListResponse } from "@/lib/api/generated";
import {
  getCurrentWorkspaceMember,
  leaveWorkspace,
  listWorkspaceMembers,
  removeWorkspaceMember,
  updateWorkspaceMemberRole,
} from "@/lib/api/generated/workspace-members/workspace-members";
import { logError, logInfo } from "@/lib/structured-log";

/**
 * 获取当前用户在 workspace 中的成员信息
 */
export const getCurrentMemberServer = createServerFn({ method: "GET" })
  .validator((data: { workspaceId: string }) => data)
  .handler(async ({ data }): Promise<MemberDetailResponse> => {
    try {
      const response = await getCurrentWorkspaceMember(data.workspaceId);

      if (response.status !== 200) {
        logError("workspace.members.me.failed", {
          status: response.status,
          workspace_id: data.workspaceId,
        });
        throw new Error(`Failed to get current member: ${response.status}`);
      }

      logInfo("workspace.members.me.success", {
        workspace_id: data.workspaceId,
        role: response.data.role,
      });
      return response.data;
    } catch (error) {
      logError("workspace.members.me.error", {
        error: error instanceof Error ? error.message : "unknown-error",
        workspace_id: data.workspaceId,
      });
      throw error;
    }
  });

/**
 * 列出 workspace 成员（支持分页和搜索）
 */
export const listMembersServer = createServerFn({ method: "GET" })
  .validator(
    (data: { workspaceId: string; page?: number; pageSize?: number; search?: string }) => data,
  )
  .handler(async ({ data }): Promise<MemberListResponse> => {
    try {
      const response = await listWorkspaceMembers(data.workspaceId, {
        page: data.page,
        page_size: data.pageSize,
        search: data.search,
      });

      if (response.status !== 200) {
        logError("workspace.members.list.failed", {
          status: response.status,
          workspace_id: data.workspaceId,
        });
        throw new Error(`Failed to list members: ${response.status}`);
      }

      logInfo("workspace.members.list.success", {
        workspace_id: data.workspaceId,
        count: response.data.total,
        page: data.page,
      });
      return response.data;
    } catch (error) {
      logError("workspace.members.list.error", {
        error: error instanceof Error ? error.message : "unknown-error",
        workspace_id: data.workspaceId,
      });
      throw error;
    }
  });

/**
 * 删除成员
 */
export const removeMemberServer = createServerFn({ method: "POST" })
  .validator((data: { workspaceId: string; userId: string }) => data)
  .handler(async ({ data }): Promise<void> => {
    try {
      const response = await removeWorkspaceMember(data.workspaceId, data.userId);

      if (response.status !== 204) {
        logError("workspace.members.remove.failed", {
          status: response.status,
          workspace_id: data.workspaceId,
          user_id: data.userId,
        });
        throw new Error(`Failed to remove member: ${response.status}`);
      }

      logInfo("workspace.members.remove.success", {
        workspace_id: data.workspaceId,
        user_id: data.userId,
      });
    } catch (error) {
      logError("workspace.members.remove.error", {
        error: error instanceof Error ? error.message : "unknown-error",
        workspace_id: data.workspaceId,
        user_id: data.userId,
      });
      throw error;
    }
  });

/**
 * 更新成员角色
 */
export const updateMemberRoleServer = createServerFn({ method: "POST" })
  .validator((data: { workspaceId: string; userId: string; role: string }) => data)
  .handler(async ({ data }): Promise<MemberDetailResponse> => {
    try {
      const response = await updateWorkspaceMemberRole(data.workspaceId, data.userId, {
        role: data.role as "workspace_owner" | "workspace_member",
      });

      if (response.status !== 200) {
        logError("workspace.members.role.update.failed", {
          status: response.status,
          workspace_id: data.workspaceId,
          user_id: data.userId,
          new_role: data.role,
        });
        throw new Error(`Failed to update member role: ${response.status}`);
      }

      logInfo("workspace.members.role.update.success", {
        workspace_id: data.workspaceId,
        user_id: data.userId,
        new_role: data.role,
      });
      return response.data;
    } catch (error) {
      logError("workspace.members.role.update.error", {
        error: error instanceof Error ? error.message : "unknown-error",
        workspace_id: data.workspaceId,
        user_id: data.userId,
      });
      throw error;
    }
  });

/**
 * 离开 workspace
 */
export const leaveWorkspaceServer = createServerFn({ method: "POST" })
  .validator((data: { workspaceId: string }) => data)
  .handler(async ({ data }): Promise<void> => {
    try {
      const response = await leaveWorkspace(data.workspaceId);

      if (response.status !== 204) {
        logError("workspace.members.leave.failed", {
          status: response.status,
          workspace_id: data.workspaceId,
        });
        throw new Error(`Failed to leave workspace: ${response.status}`);
      }

      logInfo("workspace.members.leave.success", {
        workspace_id: data.workspaceId,
      });
    } catch (error) {
      logError("workspace.members.leave.error", {
        error: error instanceof Error ? error.message : "unknown-error",
        workspace_id: data.workspaceId,
      });
      throw error;
    }
  });
